// Startup shared by both apps: pick the backend, then make sure the user is
// signed in. Renders a small status screen into `container` while it works.

import { configService, authGuardService } from './globals.js';
import { initializeAuth } from '../services/auth/index.js';
import { icons } from './icons.js';

export function applyColorMode() {
    const mode = localStorage.getItem('polyglot-mode') || 'light';
    document.documentElement.classList.toggle('dark', mode === 'dark');
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', mode === 'dark' ? '#121715' : '#F5F3EE');
}

function renderStatus(container, message) {
    container.innerHTML = `
        <div class="pg-loading" role="status">
            <span class="pg-spinner" aria-hidden="true"></span>
            <p>${message}</p>
        </div>`;
}

function renderSignIn(container, onRetry) {
    container.innerHTML = `
        <div class="pg-loading">
            <span class="pg-brand__mark" style="width:48px;height:48px;border-radius:14px">${icons.globe}</span>
            <h1 class="pg-display" style="margin:0;font-size:26px;color:var(--pg-ink)">Sign in to continue</h1>
            <p style="margin:0;max-width:320px">Polyglot needs you to sign in with Google before translating.</p>
            <button type="button" class="pg-btn pg-btn--primary" id="pg-signin-retry">Sign in with Google</button>
        </div>`;
    container.querySelector('#pg-signin-retry').addEventListener('click', onRetry);
}

/**
 * @param {HTMLElement} container element to draw status into
 * @returns {Promise<void>} resolves once signed in
 */
export async function boot(container) {
    applyColorMode();
    renderStatus(container, 'Connecting…');

    // Check sign-in alongside the backend probe (which can take a while when
    // the backend is waking up), so signed-out visitors aren't kept waiting.
    const backendReady = configService.resolveActiveEndpoint().catch((error) => {
        console.warn('Backend probe failed, using default endpoint', error);
    });

    await initializeAuth();

    if (!(await authGuardService.isAuthenticated())) {
        renderStatus(container, 'Waiting for sign-in…');
        // On tafiti.dev this goes to the shared sign-in page and doesn't come
        // back. Elsewhere it shows Google One Tap or a sign-in dialog.
        while (!(await authGuardService.ensureAuthenticated())) {
            await new Promise((resolve) => renderSignIn(container, resolve));
            renderStatus(container, 'Waiting for sign-in…');
        }
    }

    await backendReady;
}

export function registerServiceWorker() {
    if (!('serviceWorker' in navigator) || import.meta.env.DEV) return;
    window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js').catch((error) => {
            console.warn('Service worker registration failed', error);
        });
    });
}
